import React from 'react';

import cn from '@/utils/cn';

import { calloutClass, toneText, toneTint } from './tone';
import { BucketTotals, TableWarning, UnclaimedStory } from './types';

interface Props {
  tables: TableWarning[];
  unclaimed: UnclaimedStory[];
  words: BucketTotals;
}

const UnclaimedCard = ({ story }: { story: UnclaimedStory }) => (
  <li className={cn(calloutClass('error'), 'tw-px-3 tw-py-2')}>
    <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{story.title}</p>
    <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-spa-secondary">
      Story {story.storyId} · {story.paragraphs} paragraph{story.paragraphs === 1 ? '' : 's'} · {story.words} words
      — matches nothing online. New print content, or a title mismatch.
    </p>
    <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-italic tw-text-spa-secondary">“{story.snippet}”</p>
  </li>
);

const TableCard = ({ warning }: { warning: TableWarning }) => (
  <li className={cn('tw-rounded tw-px-3 tw-py-2', toneTint.warning)}>
    <p className={cn('tw-m-0 tw-text-sm', toneText.warning)}>
      {warning.count === 1 ? 'A table' : `${warning.count} tables`} in “{warning.storyTitle}”
      {warning.sectionTitle ? ` (${warning.sectionTitle})` : ' (not a reading section)'} — tables
      can’t be poured{warning.sectionTitle ? ' and their text will churn in the diff' : ''}.
    </p>
  </li>
);

// The conservation ledger: whatever the tool couldn't place is listed here, never dropped
// silently — a report whose buckets sum to the whole file can be trusted; one that only lists
// what was recognized cannot.
const UnaccountedPanel = ({ tables, unclaimed, words }: Props) => {
  const unaccounted = words.unclaimed + words.fragment;
  const pct = words.total === 0 ? 100 : Math.round(((words.total - unaccounted) / words.total) * 100);

  return (
    <div className="tw-mt-3">
      <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
        {words.total - unaccounted} of {words.total} words accounted for ({pct}%) — sections, captions,
        vocabulary, and page furniture. {unaccounted > 0
          ? `The remaining ${unaccounted} words are unmatched stories and small fragments (labels, prompts).`
          : 'Nothing is unaccounted for.'}
      </p>

      {unclaimed.length === 0 && tables.length === 0 ? (
        <p className={cn('tw-m-0 tw-mt-2 tw-text-sm', toneText.success)}>
          Every substantial story in the file is accounted for.
        </p>
      ) : (
        <ul className="tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {unclaimed.map(story => <UnclaimedCard key={story.storyId} story={story} />)}
          {tables.map(warning => <TableCard key={warning.storyTitle} warning={warning} />)}
        </ul>
      )}
    </div>
  );
};

export default UnaccountedPanel;
